/**
 * Utilidades compartidas para normalizar valores de formularios / API.
 */

/**
 * Normaliza la lada de la API (ej. "+52") al formato que usan los selects
 * ("+ 52" con espacio) para que matchee con las opciones cargadas.
 */
export function normalizeLada(rawLada: string | undefined | null): string {
  if (!rawLada) return '+ 54';
  const trimmed = rawLada.trim();
  if (!trimmed.startsWith('+')) return trimmed;
  if (trimmed.startsWith('+ ')) return trimmed;
  return `+ ${trimmed.substring(1)}`;
}

/** Coerción defensiva de booleanos del API (boolean | 0/1 | "true"/"false"). */
export function toBool(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return (
      normalized === 'true' ||
      normalized === '1' ||
      normalized === 'yes' ||
      normalized === 'si' ||
      normalized === 'sí'
    );
  }
  return false;
}

/** Convierte `YYYY-MM-DD` a `DD/MM/YYYY`; si no matchea, devuelve el input. */
export function formatIsoDateToDisplay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) {
    return iso;
  }
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/**
 * Normaliza una fecha de API / UI al valor que acepta `<input type="date">`
 * (`YYYY-MM-DD`). Soporta ISO datetime, `YYYY-MM-DD` y `DD/MM/YYYY`.
 */
export function toDateInputValue(raw: string | undefined | null): string {
  if (raw == null) return '';
  const s = String(raw).trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const isoDay = /^(\d{4}-\d{2}-\d{2})[T\s]/.exec(s);
  if (isoDay) return isoDay[1];
  const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  return '';
}

/** Extrae todos los dígitos (sin truncar). */
export function phoneDigitsAll(raw: string | undefined | null): string {
  return String(raw ?? '').replace(/\D/g, '');
}

/**
 * Extrae dígitos de teléfono. Por defecto no trunca (para validar exactitud).
 * Pasar `max` solo cuando se necesita limitar (p. ej. máscara parcial).
 */
export function phoneDigits(
  raw: string | undefined | null,
  max?: number,
): string {
  const digits = phoneDigitsAll(raw);
  return max != null ? digits.slice(0, max) : digits;
}

/**
 * Normaliza el input telefónico a solo dígitos (sin guiones ni espacios).
 * Tope suave de 15 para no saturar al pegar; la validación exige exactos 10.
 * Ejemplo: "112-345-6789" → "1123456789"
 */
export function formatPhone10Mask(raw: string | undefined | null): string {
  return phoneDigitsAll(raw).slice(0, 15);
}

/** True solo con exactamente 10 dígitos (ni menos ni más). */
export function isPhone10Complete(value: string | undefined | null): boolean {
  return phoneDigitsAll(value).length === 10;
}

/**
 * CUIT / CUIL argentino: XX-XXXXXXXX-X (11 dígitos, máscara ##-########-#).
 * Ejemplo: 20-93729750-3
 */
export const CUIT_COMPLETE_PATTERN = /^\d{2}-\d{8}-\d$/;

/** Extrae todos los dígitos del CUIT/CUIL (sin truncar). */
export function cuitDigitsAll(raw: string | undefined | null): string {
  return String(raw ?? '').replace(/\D/g, '');
}

/**
 * Máscara progresiva: 2 → 20 → 20-9 → 20-93729750 → 20-93729750-3
 * Si hay más de 11 dígitos se conservan extras (el campo sigue inválido).
 */
export function formatCuitMask(raw: string | undefined | null): string {
  const all = cuitDigitsAll(raw).slice(0, 16);
  const digits = all.slice(0, 11);
  const prefix = digits.slice(0, 2);
  const body = digits.slice(2, 10);
  const check = digits.slice(10, 11);

  let masked = prefix;
  if (digits.length > 2) masked = `${prefix}-${body}`;
  if (digits.length > 10) masked = `${prefix}-${body}-${check}`;

  if (all.length > 11) {
    return `${masked}${all.slice(11)}`;
  }
  return masked;
}

/** True solo con exactamente 11 dígitos (acepta con o sin guiones de máscara). */
export function isCuitComplete(value: string | undefined | null): boolean {
  return cuitDigitsAll(value).length === 11;
}

/**
 * Valor de CUIT/CUIL para API: solo dígitos, sin guiones.
 * Ej.: "20-93729750-3" → "20937297503"
 */
export function toCuitApiValue(raw: string | undefined | null): string {
  return cuitDigitsAll(raw);
}

/**
 * Valor de CUIT/CUIL para UI: re-aplica la máscara ##-########-# sobre dígitos.
 * API o input crudo → string con guiones para el usuario.
 */
export function toCuitDisplayValue(raw: string | undefined | null): string {
  return formatCuitMask(raw);
}

/**
 * Error en vivo para teléfono de 10 dígitos (rojo si ≠ 10).
 * Vacío solo falla post-submit.
 */
export function phone10ErrorMessage(
  value: string | undefined | null,
  submitted: boolean,
  emptyLabel = 'Número',
): string {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    return submitted ? `${emptyLabel} es obligatorio` : '';
  }
  const count = phoneDigitsAll(trimmed).length;
  if (count !== 10) {
    return 'El número debe tener 10 dígitos';
  }
  return '';
}
